//Libraries
import { FC, useState, useEffect } from 'react';
import { Grid } from '@mui/material';
import { useNavigate, useLocation, } from 'react-router-dom';

//Interfaces & Typing
import { APP_URLS } from 'src/constants';
import { useReferences } from 'src/services';

// Styling and Icons
import { usStateOptions } from "../../constants/usStates";

//Components
import { FieldRow, Divider, Title } from 'src/generic/component';
import { Input, Dropdown } from 'src/generic/component/form';
import { AiSearch, DiSearch } from 'src/org/component/orgSearch';

/**
 * This component is only responsible for extracting
 * the summary data from the navigation and instantiating
 * the OpsForm.  This allows us to use the OpsForm in modals
 * or other implementations without having to re-write the subform
 */
export const SearchPage: FC = () => {
    const { countries, regions } = useReferences() || {}
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as any;
    const type = state?.type;
    const search = state?.search || {};

    const [searchParams, setSearchParams] = useState<any>(search);

    // Always clear province when type or country  changes
    useEffect(() => {
    setSearchParams((prev: any) => ({ ...prev, province: undefined }));
    }, [type, searchParams.countryIsoCd]);
    
    // Helper to get province options for selected country
    const getProvinceOptions = (countryIsoCd: string) => {
        if (!regions || !countryIsoCd || countryIsoCd === 'US') return [];
        return regions
            .filter((region) => region.countryIsoCd === countryIsoCd)
            .map((region) => ({  key: region.label, label: region.label  }));
    };

    return <div>
        { type === 'ai' && <Title>Search School Codes Search</Title>}
        { type === 'di' && <Title>Score Send Codes Search</Title>}
        <FieldRow>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <Input onChange={(e) => {
                        setSearchParams({...searchParams, name: e.target.value});
                    }} placeholder='Name' value={searchParams.name}/>
                </Grid>
                <Grid item xs={6}>
                    <Input onChange={(e) => {
                        setSearchParams({...searchParams, text: e.target.value});
                    }} placeholder='Name, Addresses, City...' value={searchParams.text}/>
                </Grid>
                { type === 'ai' &&
                <Grid item xs={4}>
                    <Input onChange={(e) => {
                        setSearchParams({...searchParams, aiCode: e.target.value});
                    }} placeholder='School Code' value={searchParams.aiCode}/>
                </Grid>
                }
                { type === 'di' &&
                <Grid item xs={4}>
                    <Input onChange={(e) => {
                        setSearchParams({...searchParams, diCode: e.target.value});
                    }} placeholder='Score Send Code' value={searchParams.diCode}/>
                </Grid>
                }
                <Grid item xs={4}>
                    {countries && <Dropdown values={countries.map((option) => ({ key: option.value, label: option.label }))}
                        onChange={(e) => {
                            setSearchParams({
                                ...searchParams,
                                countryIsoCd: e.target.value,
                                state: undefined,
                                postalCd: undefined,
                                province: undefined
                            });
                        }}
                        value={searchParams.countryIsoCd} />}
                </Grid>
                {searchParams.countryIsoCd &&
                    <Grid item xs={4}>
                        {searchParams.countryIsoCd === 'US' ? (
                            <div>
                                <Dropdown
                                    values={usStateOptions.map((option) => ({ key: option.value, label: option.label }))}
                                    onChange={(e) => {
                                    setSearchParams({ ...searchParams, state: e.target.value, postalCd: undefined, province: undefined });
                                    }}
                                    value={searchParams.state}
                                />
                            </div>):
                            (
                                <>
                                    <Dropdown
                                        values={getProvinceOptions(searchParams.countryIsoCd)}
                                        onChange={(e) => {
                                            setSearchParams({ ...searchParams, province: e.target.value, state: undefined, postalCd: undefined });
                                        }}
                                        value={searchParams.province}
                                        placeholder="Province"
                                    /> &nbsp;&nbsp;
                                    <Input
                                        onChange={(e) => {
                                            setSearchParams({ ...searchParams, postalCd: e.target.value });
                                        }}
                                        placeholder="Postal Code"
                                        value={searchParams.postalCd}
                                    />
                                </>
                            )
                            
                        }
                    </Grid>
                }
            </Grid>
        </FieldRow>
        <Divider/>
        { type === 'ai' &&
        <AiSearch
            params={{
                aicode: searchParams.aiCode,
                name: searchParams.name,
                intl_postal_cd: searchParams.postalCd,
                state: searchParams.state,
                iso: searchParams.countryIsoCd,
                text: searchParams.text,
                province: searchParams?.province
            }}
            onSelect={(match) => {
                navigate(APP_URLS.PROFILE, {
                    state: {
                        aiCode: match.aiCode,
                        orgId: match.orgId,
                        type: 'ai',
                        search: {
                            aiCode: searchParams.aiCode,
                            name: searchParams.name,
                            intl_postal_cd: searchParams.postalCd,
                            state: searchParams.state,
                            countryIsoCd: searchParams.countryIsoCd,
                            text: searchParams.text
                        }
                    }
                });
            }}
        />
        }
        { type === 'di' &&
        <DiSearch
            params={{
                dicode: searchParams.diCode,
                name: searchParams.name,
                intl_postal_cd: searchParams.postalCd,
                state: searchParams.state,
                iso: searchParams.countryIsoCd,
                text: searchParams.text,
                province: searchParams?.province
            }}
            onSelect={(match) => {
                navigate(APP_URLS.DIPROFILE, {
                    state: {
                        diCode: match.diCode,
                        orgId: match.orgId,
                        type: 'di',
                        search: {
                            diCode: searchParams.diCode,
                            name: searchParams.name,
                            intl_postal_cd: searchParams.postalCd,
                            state: searchParams.state,
                            countryIsoCd: searchParams.countryIsoCd,
                            text: searchParams.text
                        }
                    }
                });
            }}
        />
    }
    </div>;
};
